import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import {
  groupSelectOptions,
  type SelectOption,
} from "@/components/features/matter/file-repo/GenerateDocument/CreatableSelect";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  LoadingSpinner,
  Switch,
  Typography,
} from "@/components/ui";
import { getItemFromPresignedUrlOptions } from "@/lib/data/react-query/s3";
import {
  BROWSER_PREVIEW_VARIABLE_MARKER,
  buildBrowserDocxPreview,
} from "@/lib/utils/previewDocxTemplate";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { cn } from "@/lib/utils/cn";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

type TemplateFile = {
  fileName: string;
  presignedURLKey: string;
};

type BrowserDocxTemplatePreviewProps = {
  buildInputs: () => Record<string, string>;
  canSelectVariable?: (variable: string) => boolean;
  fileType?: "firm-file" | "matter-file" | "matter-template";
  flushLayout?: boolean;
  getVariableConfig: (variable: string) => PreviewVariableConfig;
  getVariableHighlight?: (variable: string) => string | null;
  onVariableSelect: (variable: string, value: string) => void;
  open: boolean;
  templateFile: TemplateFile;
  templateId: string;
};

export type PreviewVariableConfig = {
  label: string;
  options: SelectOption[];
};

type ActiveField = {
  bottom: number;
  left: number;
  top: number;
  variable: string;
};

type BrowserPreview = {
  file: File;
  templateId: string;
  url: string;
};

const canSelectEveryVariable = () => true;

export const BrowserDocxTemplatePreview = ({
  buildInputs,
  canSelectVariable = canSelectEveryVariable,
  fileType = "matter-template",
  flushLayout = false,
  getVariableConfig,
  getVariableHighlight,
  onVariableSelect,
  open,
  templateFile,
  templateId,
}: BrowserDocxTemplatePreviewProps) => {
  const isDocxFile =
    getFileNameParts(templateFile.fileName).fileExtension === "docx";
  const {
    data: templateResponse,
    isError: isTemplateError,
    refetch: refetchTemplate,
  } = useQuery({
    ...getItemFromPresignedUrlOptions(fileType, templateFile.presignedURLKey),
    enabled: open && isDocxFile && Boolean(templateFile.presignedURLKey),
  });
  const [preview, setPreview] = useState<BrowserPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [interactive, setInteractive] = useState(true);
  const [activeField, setActiveField] = useState<ActiveField | null>(null);
  const [customValue, setCustomValue] = useState("");
  const generationRef = useRef(0);
  const interactiveRef = useRef(interactive);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const getVariableConfigRef = useRef(getVariableConfig);
  const onVariableSelectRef = useRef(onVariableSelect);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef({ left: 0, top: 0 });
  const templateBufferRef = useRef<{
    buffer: ArrayBuffer;
    templateId: string;
  } | null>(null);
  getVariableConfigRef.current = getVariableConfig;
  onVariableSelectRef.current = onVariableSelect;
  interactiveRef.current = interactive;

  useEffect(() => {
    if (!open || !isDocxFile || !templateResponse) {
      setIsUpdating(false);
      return;
    }

    const generation = ++generationRef.current;
    setIsUpdating(true);
    setError(null);

    const timeout = window.setTimeout(() => {
      const buildPreview = async () => {
        try {
          let templateBuffer = templateBufferRef.current?.buffer;
          if (
            !templateBuffer ||
            templateBufferRef.current?.templateId !== templateId
          ) {
            templateBuffer = await templateResponse.clone().arrayBuffer();
            templateBufferRef.current = { buffer: templateBuffer, templateId };
          }

          const blob = await buildBrowserDocxPreview(
            templateBuffer,
            buildInputs(),
            { interactive },
          );
          if (generation !== generationRef.current) return;

          const previewFileName = templateFile.fileName.endsWith(".docx")
            ? templateFile.fileName
            : `${templateFile.fileName || "template-preview"}.docx`;
          const file = new File([blob], previewFileName, { type: blob.type });
          const url = URL.createObjectURL(blob);
          setPreview({ file, templateId, url });
        } catch (previewError) {
          if (generation !== generationRef.current) return;
          console.error(
            "Unable to build browser template preview",
            previewError,
          );
          setError(
            "The approximate preview could not be updated. The original template is shown instead.",
          );
        } finally {
          if (generation === generationRef.current) setIsUpdating(false);
        }
      };

      void buildPreview();
    }, 300);

    return () => {
      window.clearTimeout(timeout);
      generationRef.current += 1;
    };
  }, [
    buildInputs,
    fileType,
    interactive,
    isDocxFile,
    open,
    templateFile.fileName,
    templateId,
    templateResponse,
  ]);

  useEffect(() => {
    const previewUrl = preview?.url;
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [preview?.url]);

  const activePreview = preview?.templateId === templateId ? preview : null;
  const previewFile = useMemo(
    () =>
      activePreview
        ? { file: activePreview.file, url: activePreview.url }
        : null,
    [activePreview],
  );
  const handlePreviewScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    scrollPositionRef.current = {
      left: container.scrollLeft,
      top: container.scrollTop,
    };
    setActiveField(null);
  }, []);
  const handleScrollContainerReady = useCallback(
    (container: HTMLDivElement | null) => {
      scrollContainerRef.current?.removeEventListener(
        "scroll",
        handlePreviewScroll,
      );
      scrollContainerRef.current = container;
      container?.addEventListener("scroll", handlePreviewScroll, {
        passive: true,
      });
    },
    [handlePreviewScroll],
  );
  const restorePreviewScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo(scrollPositionRef.current);
  }, []);
  const decoratePreviewFields = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const makeClickable = (
      fieldElements: HTMLSpanElement[],
      variable: string,
    ) => {
      if (fieldElements.length === 0) return;

      const highlight = getVariableHighlight?.(variable);
      if (highlight) {
        fieldElements.forEach((element) => {
          element.style.background = highlight;
          element.style.color = "inherit";
        });
      }

      if (!canSelectVariable(variable)) return;

      const elements = [...fieldElements];
      const lastElement = elements.at(-1);
      if (lastElement) {
        const caret = lastElement.ownerDocument.createElement("span");
        caret.textContent = "▾";
        caret.setAttribute("aria-hidden", "true");
        caret.style.display = "inline-flex";
        caret.style.alignItems = "center";
        caret.style.justifyContent = "center";
        caret.style.width = "1em";
        caret.style.height = "1em";
        caret.style.marginLeft = "0.15em";
        caret.style.fontFamily = "ui-sans-serif, system-ui, sans-serif";
        caret.style.fontSize = "0.65em";
        caret.style.lineHeight = "1";
        caret.style.pointerEvents = "none";
        caret.style.verticalAlign = "0.08em";
        lastElement.appendChild(caret);
      }

      const openDropdown = () => {
        if (!interactiveRef.current) return;
        const rects = elements.map((element) =>
          element.getBoundingClientRect(),
        );
        setCustomValue("");
        setActiveField({
          variable,
          left: Math.min(...rects.map(({ left }) => left)),
          top: Math.min(...rects.map(({ top }) => top)),
          bottom: Math.max(...rects.map(({ bottom }) => bottom)),
        });
      };

      elements.forEach((element, index) => {
        element.style.cursor = "pointer";
        element.setAttribute("role", "button");
        element.setAttribute("title", `Choose ${variable}`);
        element.tabIndex = index === 0 ? 0 : -1;
        element.addEventListener("click", openDropdown);
        element.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openDropdown();
          }
        });
      });
    };

    container.querySelectorAll("p").forEach((paragraph) => {
      const textNodes: { end: number; node: Text; start: number }[] = [];
      const walker = paragraph.ownerDocument.createTreeWalker(
        paragraph,
        NodeFilter.SHOW_TEXT,
      );
      let offset = 0;
      let node = walker.nextNode();
      while (node) {
        const text = node.textContent ?? "";
        textNodes.push({
          node: node as Text,
          start: offset,
          end: offset + text.length,
        });
        offset += text.length;
        node = walker.nextNode();
      }

      const text = textNodes
        .map(({ node: textNode }) => textNode.data)
        .join("");
      Array.from(text.matchAll(/\{\{([^{}]+)\}\}/g)).forEach((match) => {
        if (!match[1]) return;
        const start = match.index;
        const end = start + match[0].length;
        const elements = Array.from(
          new Set(
            textNodes
              .filter((segment) => segment.end > start && segment.start < end)
              .map(({ node: textNode }) =>
                textNode.parentElement?.closest("span"),
              )
              .filter((element): element is HTMLSpanElement => element != null),
          ),
        );
        if (elements.length === 0) return;

        makeClickable(elements, match[1].trim());
      });
    });

    container.querySelectorAll<HTMLSpanElement>("span").forEach((marker) => {
      if (!marker.textContent?.startsWith(BROWSER_PREVIEW_VARIABLE_MARKER)) {
        return;
      }
      const valueElement = marker.nextElementSibling;
      if (!(valueElement instanceof HTMLSpanElement)) return;
      const variable = decodeURIComponent(
        marker.textContent.slice(BROWSER_PREVIEW_VARIABLE_MARKER.length),
      );
      makeClickable([valueElement], variable);
    });
  }, [canSelectVariable, getVariableHighlight]);
  const handleDocxRendered = useCallback(() => {
    restorePreviewScroll();
    if (interactiveRef.current) decoratePreviewFields();
  }, [decoratePreviewFields, restorePreviewScroll]);

  useEffect(() => {
    if (!activeField) return;
    const closeDropdown = (event: PointerEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setActiveField(null);
      }
    };
    document.addEventListener("pointerdown", closeDropdown);
    return () => {
      document.removeEventListener("pointerdown", closeDropdown);
    };
  }, [activeField]);

  const activeConfig = activeField
    ? getVariableConfigRef.current(activeField.variable)
    : null;
  const selectActiveValue = (value: string) => {
    if (!activeField || !value.trim()) return;
    onVariableSelectRef.current(activeField.variable, value.trim());
    setActiveField(null);
    setCustomValue("");
  };
  const dropdownPortalTarget = activeField
    ? (scrollContainerRef.current?.closest('[role="dialog"]') ?? document.body)
    : null;
  const portalRect = dropdownPortalTarget?.getBoundingClientRect();
  const portalWidth =
    dropdownPortalTarget instanceof HTMLElement
      ? dropdownPortalTarget.clientWidth
      : window.innerWidth;
  const scaleX = portalRect?.width
    ? portalRect.width / Math.max(portalWidth, 1)
    : 1;
  const scaleY =
    dropdownPortalTarget instanceof HTMLElement && portalRect?.height
      ? portalRect.height / Math.max(dropdownPortalTarget.clientHeight, 1)
      : 1;
  const dropdownLeft = activeField
    ? Math.max(
        8,
        Math.min(
          (activeField.left - (portalRect?.left ?? 0)) / scaleX,
          portalWidth - 388,
        ),
      )
    : 8;
  const showDropdownBelow = activeField
    ? window.innerHeight - activeField.bottom >= 330
    : true;
  const dropdownTop = activeField
    ? ((showDropdownBelow ? activeField.bottom : activeField.top) -
        (portalRect?.top ?? 0)) /
      scaleY
    : 0;
  const dropdown =
    activeField && activeConfig && dropdownPortalTarget
      ? createPortal(
          <div
            ref={dropdownRef}
            data-modal-popover="true"
            className="bg-popover text-popover-foreground absolute z-[120] w-[min(380px,calc(100vw-16px))] overflow-hidden rounded-md border shadow-xl"
            style={{
              left: dropdownLeft,
              top: dropdownTop + (showDropdownBelow ? 6 : -6),
              transform: showDropdownBelow ? undefined : "translateY(-100%)",
            }}
          >
            <div className="border-b px-3 py-2 text-sm font-medium">
              Choose {activeConfig.label}
            </div>
            <Command>
              <CommandInput
                autoFocus
                placeholder={`Search or enter ${activeConfig.label}`}
                value={customValue}
                onValueChange={setCustomValue}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && customValue.trim()) {
                    event.preventDefault();
                    selectActiveValue(customValue);
                  }
                }}
              />
              <CommandList className="max-h-[260px]">
                <CommandEmpty>
                  {customValue.trim()
                    ? `Press Enter to use “${customValue.trim()}”`
                    : "No options available. Type a value to use it."}
                </CommandEmpty>
                {groupSelectOptions(activeConfig.options).map(
                  ([group, groupedOptions], groupIndex) => (
                    <CommandGroup
                      key={group || "options"}
                      heading={group || undefined}
                      className={groupIndex > 0 ? "border-t pt-1" : undefined}
                    >
                      {groupedOptions.map((option) => (
                        <CommandItem
                          key={
                            option.id ??
                            `${option.value}-${option.context ?? ""}`
                          }
                          value={
                            option.id ??
                            `${option.label} ${option.context ?? ""}`
                          }
                          keywords={[
                            option.label,
                            option.value,
                            option.context ?? "",
                          ]}
                          onSelect={() => {
                            selectActiveValue(option.value);
                          }}
                        >
                          <div className="flex flex-col">
                            <span>{option.label}</span>
                            {option.context && (
                              <span className="text-muted-foreground text-xs">
                                {option.context}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ),
                )}
              </CommandList>
            </Command>
          </div>,
          dropdownPortalTarget,
        )
      : null;

  return (
    <div className="flex min-h-[420px] flex-col gap-3">
      <div
        className={cn(
          "bg-card sticky top-0 z-30 space-y-1 border-b px-6 py-5",
          !flushLayout && "-mx-6 -mt-5",
        )}
      >
        <div className="flex flex-wrap items-center gap-4">
          <Typography weight="semibold">Document preview</Typography>
          <label className="flex cursor-pointer items-center gap-3 text-xs font-medium">
            <Switch
              checked={interactive}
              aria-label="Interactive preview"
              onCheckedChange={(checked) => {
                setInteractive(checked);
                setActiveField(null);
              }}
            />
            Interactive preview
          </label>
        </div>
        <p className="text-muted-foreground text-xs">
          This preview may differ slightly from the final document.
        </p>
        {(error || isTemplateError) && (
          <p role="alert" className="text-destructive text-xs">
            {error || "Unable to load the template file. Please retry."}
          </p>
        )}
      </div>
      <div
        className={cn(
          "relative flex min-h-[360px] flex-1",
          flushLayout && "px-6 pt-3 pb-6",
        )}
      >
        {previewFile && activePreview ? (
          <FileViewer
            fileType="matter-template"
            file={previewFile}
            showDownload={false}
            showTitle={false}
            viewerClassName="bg-card min-h-[360px] items-stretch justify-stretch"
            wrapperClassName="min-h-0 w-full flex-1"
            compactDocxPreview={false}
            onDocxScrollContainerReady={handleScrollContainerReady}
            onDocxRendered={handleDocxRendered}
          />
        ) : (
          <div className="bg-card flex min-h-[360px] flex-1 items-center justify-center rounded-md border">
            {isTemplateError || !templateFile.presignedURLKey ? (
              <div className="space-y-3 p-4 text-center">
                <p className="text-muted-foreground text-sm">
                  {!templateFile.presignedURLKey
                    ? "This template has no file attached."
                    : "Unable to load the template file."}
                </p>
                {isTemplateError ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      void refetchTemplate();
                    }}
                  >
                    Retry
                  </Button>
                ) : null}
              </div>
            ) : error ? (
              <p role="alert" className="text-destructive p-4 text-sm">
                {error}
              </p>
            ) : (
              <LoadingSpinner className="h-8 w-8" />
            )}
          </div>
        )}
        {isUpdating && activePreview && (
          <div className="bg-card/20 pointer-events-none absolute inset-0 z-20 flex items-start justify-center rounded-md pt-4 backdrop-blur-[1px]">
            <div className="bg-card text-foreground flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium shadow-md">
              <LoadingSpinner className="h-4 w-4" />
              Updating preview…
            </div>
          </div>
        )}
      </div>
      {dropdown}
    </div>
  );
};
